import {useEffect, useState} from "react";
import {useOverlayActivity} from "components/useOverlayActivity";
import {SheetTransition} from "transitions/SheetTransition";
import {overlay} from "theme/tokens";
import {useLocale, useMessages} from "i18n";
import {useUser} from "UserProvider";
import {useCheckoutDocument} from "pay/useCheckoutDocument";
import {SubscriptionElementsCheckout} from "pay/SubscriptionElementsCheckout";
import {OfferOption} from "upsell/OfferOption";
import {Options} from "upsell/OfferOption.styles";
import {PlanIllustration} from "upsell/PlanIllustration";
import {radioTabStop} from "upsell/radioGroup";
import {useRadioGroupKeys} from "upsell/useRadioGroupKeys";
import {
	Art, ArtCopy, ArtEyebrow, ArtPitch, ArtSceneBox, BackToPlan, CloseButton, Content, Layout,
} from "upsell/CompactUpgradeDialog.styles";
import {
	backFromPayment, continuePayDialog, mayContinue, PAY_DIALOG_START, planRowFigures,
	type PayDialogState, type PayRow,
} from "../overLimit/overLimitPayDialog";
import type {OverLimitPlanOffer, OverLimitPricing} from "../overLimit/overLimitOffer";
import {preselectedPlanRow, type SizedPlanRows} from "../overLimit/sizedPlanDialog";
import type {OverLimitPlanPurchase} from "../overLimit/useOverLimitPlanPurchase";
import {Continue, Lede, PayDialog, Terms, Title} from "./OverLimitCheckoutDialog.styles";

interface Props {
	/**
	 * Whether the dialog is on screen. Its own flag since kitecorp/transferify-ui#1844: the dialog opens
	 * BEFORE any session exists, to ask for the express consent that has to precede the mint.
	 */
	open: boolean;
	/** Which rule prices the send; the rows describe a hosted or an Instant transfer accordingly. */
	pricing: OverLimitPricing;
	/** The selection's size as the sender reads it. */
	sizeLabel: string;
	/** The plan row (kitecorp/transferify-ui#3229), or null for a dialog with no row. */
	planOffer: OverLimitPlanOffer | null;
	/**
	 * The sized plan dialog's rows (kitecorp/transferify-ui#3800): every plan that holds the drop, the one in view and
	 * the sender's pick, or null while no wall holds the dialog (it is closed or leaving, and stays mounted). The choosing
	 * step lists the rows, and `planOffer` is the row in view. Both plan walls pass them; only the lede and each row's
	 * detail differ (the Instant wall's say Instant is free up to a size).
	 */
	sized: SizedPlanRows | null;
	/** The plan row's purchase, held by the form's view model. */
	plan: OverLimitPlanPurchase;
	/** The sender chose the plan row and pressed Continue. */
	onContinuePlan: () => void;
	/** The plan's order was placed on our button: the dialog closes and the composer waits for the plan. */
	onPlanPlaced: () => void;
	/** Escape or the ✕: abandon this attempt. THE SELECTION SURVIVES, and so does a settled allowance. */
	onClose: () => void;
	/**
	 * Where focus goes once the dialog has finished leaving. Supplied by the host because the element is
	 * the host's — the over-limit strip, which is what replaces this dialog on screen.
	 */
	onTransitionExited: () => void;
}

/**
 * The over-cap pay dialog (ui#1454): the plans that lift the wall since kitecorp/transferify-ui#3800, each as its first
 * month at a fixed price. No one-off row is offered any more (kitecorp/transferify-ui#3916).
 *
 * <b>The compact upgrade dialog's layout</b>: the plan's own scene on the left, and on the right the radio rows in
 * the exit offer's option style, the smallest plan chosen when it opens ({@link preselectedPlanRow}), so Continue is
 * live at once. While no rows are held (the dialog is closed or leaving) the right column holds the close control alone.
 * Continue swaps the right column for the payment step: Back, then the plan's order form
 * (`pay/SubscriptionElementsCheckout`): Stripe's fields inside our dialog once the consent tick has minted the
 * Session, the grey summary box, the consent, and OUR labelled order button with nothing interactive after it. The
 * chosen row is not restated above the form (owner decision 2026-10-09): on its own it read as a lone selected radio,
 * and the summary box already says what is bought. In German the strip's button and Continue read as navigation; only
 * the form's button names the payment (§ 312j Abs. 3 BGB, OLG Celle 13 U 21/26).
 *
 * <b>Every figure is the api's.</b> The plan row's first month, renewal and limit come from the quote's
 * `planOffer`, formatted by `planRowFigures`; the plan's name and pitch are the catalogue's. The row states a price STRUCTURE, a first month and then the monthly price, never a
 * reduction; the plan form's summary, like every checkout's, states no subtotal and no discount line
 * (`orderSummaryLines`).
 *
 * <b>The ✕ closes and nothing else follows</b> (the close offer left the ui, kitecorp/transferify-ui#3781). The scrim
 * is inert while a card number may be on screen: a stray click outside would throw it away with no way back.
 *
 * Every rule here is React-free in `../overLimit/overLimitPayDialog`; this file holds the steps' state and the
 * markup. Focus restore is APP-OWNED, as it was: a PLACED order closes the dialog and switches the strip to
 * "confirming" in the same commit, so the opener is gone and MUI's trap would restore onto a detached node; the
 * host aims the exit callback at the strip that replaced the dialog on screen.
 */
export function OverLimitCheckoutDialog(
	{open, pricing, sizeLabel, planOffer, sized, plan, onContinuePlan, onPlanPlaced, onClose, onTransitionExited}: Props,
) {
	const {sender, pricing: catalogue, checkout} = useMessages();
	const copy = sender.overLimit.payment;
	const {locale} = useLocale();
	const [user] = useUser();
	const observedExit = useOverlayActivity(open);
	// ONE selection for this open (kitecorp/transferify-ui#2562): the plan's form reads it at its tick, and the
	// identity travels with the session it mints.
	const contract = useCheckoutDocument(open);
	const [storedState, setState] = useState<PayDialogState>(PAY_DIALOG_START);
	// The sized rows open on the smallest plan chosen, so Continue is live at once; the payment step is kept.
	const state = sized === null ? storedState : preselectedPlanRow(storedState);
	const continueRows: readonly PayRow[] = sized !== null && sized.plans.length > 0 ? ["plan"] : [];
	const figures = planRowFigures(planOffer, locale, catalogue.compare.unlimited);
	// The sized rows: one per plan that holds the drop, with their own arrow keys and tab stop. The hook runs on
	// every render, as hooks must, and is idle while `sized` is null.
	const sizedRows = (sized?.plans ?? []).flatMap(offer => {
		const row = planRowFigures(offer, locale, catalogue.compare.unlimited);
		return row === null ? [] : [row];
	});
	const sizedIds = sizedRows.map(row => row.plan);
	const sizedKeys = useRadioGroupKeys(sizedIds, sized?.chosen ?? null, plan => sized?.choose(plan));
	const sizedTabStop = radioTabStop(sizedIds, sized?.chosen ?? null);

	// Re-seeded on CLOSE rather than on open, so nothing races the first render of a reopen: a reader returning
	// meets the choosing step again, not the payment step they left without acting on.
	useEffect(() => {
		if (!open) {
			setState(PAY_DIALOG_START);
		}
	}, [open]);

	const exited = () => {
		observedExit();
		onTransitionExited();
	};
	const instant = pricing === "instant";
	const paying = state.step === "pay" ? state.choice : null;
	const cardOnScreen = paying === "plan" && plan.clientSecret !== null;
	const lede = instant ? copy.sized.ledeInstant : copy.sized.lede(sizeLabel);
	const planRow = figures === null ? null : {
		name: catalogue.planNames[figures.plan],
		terms: copy.plan.terms(figures.firstMonth, figures.renewal),
	};
	// Stripe needs a customer email to complete a Session; an unverified SSO signup's customer may carry none.
	const buyerEmail = user.email === "" ? undefined : user.email;

	const continueWithChoice = () => {
		const next = continuePayDialog(state, continueRows);
		if (next === state) {
			return;
		}
		onContinuePlan();
		setState(next);
	};
	// Back drops the plan's Session and consent, so a second Continue meets a fresh form; the row stays chosen.
	const back = () => {
		plan.dismiss();
		setState(backFromPayment(state));
	};

	return (
		<PayDialog
			open={open}
			// The scrim closes nothing while a card number may be on screen; Escape and the ✕ still do, and the
			// explicit control keeps that honest on a touch device, which has no Escape key.
			onClose={(_event: object, reason: string) => {
				if (reason === "backdropClick" && cardOnScreen) {
					return;
				}
				onClose();
			}}
			maxWidth={false}
			slots={{transition: SheetTransition}}
			slotProps={{
				transition: {onExited: exited},
				backdrop: {style: {background: overlay.scrim}},
				// The paper carries role="dialog", so the accessible name belongs there, the same placement the
				// compact dialog and the exit offer use.
				paper: {"aria-label": copy.sized.title},
			}}
			// The app owns this restore rather than MUI's trap: on the COMPLETED path the opener is already
			// unmounted, so MUI would restore onto a detached node and drop focus to `<body>`.
			disableRestoreFocus
		>
			<Layout data-testid="over-limit-pay-dialog">
				{planRow !== null && figures !== null && (
					<Art data-testid="over-limit-pay-art">
						<ArtSceneBox>
							<PlanIllustration plan={figures.plan}/>
						</ArtSceneBox>
						<ArtCopy>
							<ArtEyebrow>{planRow.name}</ArtEyebrow>
							{/* The tier's OWN lead bullet, as the compact dialog shows it: never a second marketing line. */}
							<ArtPitch>{catalogue.features[figures.plan][0]}</ArtPitch>
						</ArtCopy>
					</Art>
				)}
				<Content>
					<CloseButton type="button" onClick={onClose} aria-label={copy.close}>✕</CloseButton>
					{paying === null && sized !== null && (
						<>
							<Title>{copy.sized.title}</Title>
							<Lede>{lede}</Lede>
							<Options role="radiogroup" aria-label={copy.sized.optionsAria} onKeyDown={sizedKeys.onKeyDown}>
								{sizedRows.map(row => (
									<OfferOption
										key={row.plan}
										selected={sized.chosen === row.plan}
										tabStop={sizedTabStop === row.plan}
										onSelect={() => sized.choose(row.plan)}
										selectedRef={sizedKeys.selectedRef}
										testId={`over-limit-sized-row-${row.plan}`}
										title={copy.sized.row(catalogue.planNames[row.plan], row.firstMonth, row.renewal)}
										detail={instant ? copy.sized.detailInstant(row.limit) : copy.sized.detail(row.limit)}
									/>
								))}
							</Options>
							<Terms data-testid="over-limit-sized-terms">{copy.sized.terms}</Terms>
							<Continue
								type="button"
								disabled={!mayContinue(state, continueRows)}
								onClick={continueWithChoice}
								data-testid="over-limit-continue"
							>
								{copy.sized.continueWith(catalogue.planNames[sized.chosen])}
							</Continue>
						</>
					)}
					{paying !== null && (
						<>
							<BackToPlan type="button" onClick={back} data-testid="over-limit-back">
								{copy.back}
							</BackToPlan>
							{paying === "plan" && planRow !== null && figures !== null && (
								<>
									{/*
									  * The plan's order, concluded here on our button (kitecorp/transferify-ui#2582): the statutory
									  * block, the express consent and the order button over Stripe's Payment Element. The intro
									  * terms are the two-price line. The summary under it states no subtotal and no discount line,
									  * like every checkout's, so the row stays a price structure and never a reduction (the legal
									  * checks of 2026-10-03 and 2026-10-30).
									  */}
									<SubscriptionElementsCheckout
										plan={figures.plan}
										clientSecret={plan.clientSecret}
										collectsShippingAddress={plan.collectsShippingAddress}
										contractDocument={contract.document}
										awaitingDocument={contract.awaitingDocument}
										consented={plan.acknowledged}
										buyerEmail={buyerEmail}
										onConsentChange={next => plan.consent(next, contract.latestRead())}
										minting={plan.busy}
										monthlyPrice={figures.renewal}
										introTerms={planRow.terms}
										firstCharge={figures.firstMonth}
										consentTestId="over-limit-plan-consent"
										mintRefusal={plan.error === null ? null : checkout.errors[plan.error]}
										onPlaced={onPlanPlaced}
									/>
								</>
							)}
						</>
					)}
				</Content>
			</Layout>
		</PayDialog>
	);
}
